import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, FileText, Trash2, X, Loader2 } from "lucide-react";
import { api, openDocument } from "@/lib/api";
import { CASE_STATUSES } from "@/lib/constants";
import { toast } from "sonner";

const emptyForm = { case_number: "", title: "", parties: [], next_hearing_date: "", status: "Active", court_order: "" };

export const CaseRecordModal = ({ open, onOpenChange, editing, onSaved }) => {
  const [form, setForm] = useState(emptyForm);
  const [partyInput, setPartyInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docs, setDocs] = useState([]);
  const [uploadsEnabled, setUploadsEnabled] = useState(true);
  const [uploadMaxMb, setUploadMaxMb] = useState(20);
  const caseId = editing?.id;

  useEffect(() => {
    if (!open) return;
    api.get("/stats").then(({ data }) => {
      setUploadsEnabled(data.uploads_enabled !== false);
      if (data.max_upload_mb) setUploadMaxMb(data.max_upload_mb);
    }).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (editing) {
      setForm({
        case_number: editing.case_number,
        title: editing.title,
        parties: editing.parties || [],
        next_hearing_date: editing.next_hearing_date || "",
        status: editing.status || "Active",
        court_order: editing.court_order || "",
      });
      setDocs(editing.documents || []);
    } else {
      setForm(emptyForm);
      setDocs([]);
    }
    setPartyInput("");
  }, [editing, open]);

  const addParty = () => {
    const v = partyInput.trim();
    if (v) {
      setForm((f) => ({ ...f, parties: [...f.parties, v] }));
      setPartyInput("");
    }
  };

  const save = async () => {
    if (!form.case_number.trim() || !form.title.trim()) {
      toast.error("Case number and title are required");
      return;
    }
    setSaving(true);
    try {
      if (caseId) {
        await api.put(`/cases/${caseId}`, form);
        toast.success("Case record updated");
      } else {
        await api.post("/cases", form);
        toast.success("Case record saved to DB");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save case");
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !caseId) {
      if (!caseId) toast.error("Save the case first, then upload documents");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/cases/${caseId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDocs(data.documents || []);
      toast.success("Document uploaded to storage bucket");
      onSaved?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeDoc = async (docId) => {
    try {
      const { data } = await api.delete(`/cases/${caseId}/documents/${docId}`);
      setDocs(data.documents || []);
      toast.success("Document removed");
      onSaved?.();
    } catch {
      toast.error("Failed to remove document");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-[--w-border] bg-[--w-card] text-[--w-text] sm:max-w-lg" data-testid="case-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{caseId ? "Edit Case Record" : "New Case Record"}</DialogTitle>
          <DialogDescription className="text-[--w-text-2]">
            Case details stored here are searched when a lawyer sends this case number on WhatsApp.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[--w-text-2]">Case Number</Label>
              <Input
                data-testid="case-input-number"
                value={form.case_number}
                onChange={(e) => setForm({ ...form, case_number: e.target.value })}
                placeholder="CIV-2026-8890"
                className="mt-1 border-[--w-border] bg-[--w-bg] font-mono-data text-sm"
              />
            </div>
            <div>
              <Label className="text-xs text-[--w-text-2]">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="case-input-status" className="mt-1 border-[--w-border] bg-[--w-bg] text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-[--w-border] bg-[--w-card-2] text-[--w-text]">
                  {CASE_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs text-[--w-text-2]">Case Title</Label>
            <Input
              data-testid="case-input-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Smith v. Apex Technologies"
              className="mt-1 border-[--w-border] bg-[--w-bg] text-sm"
            />
          </div>

          <div>
            <Label className="text-xs text-[--w-text-2]">Parties</Label>
            <div className="mt-1 flex gap-2">
              <Input
                data-testid="case-input-party"
                value={partyInput}
                onChange={(e) => setPartyInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addParty())}
                placeholder="Add a party and press Enter"
                className="border-[--w-border] bg-[--w-bg] text-sm"
              />
              <Button type="button" onClick={addParty} variant="secondary" className="bg-stone-700 hover:bg-stone-600">Add</Button>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {form.parties.map((p, i) => (
                <span key={i} className="flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs text-blue-700">
                  {p}
                  <button onClick={() => setForm((f) => ({ ...f, parties: f.parties.filter((_, j) => j !== i) }))}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs text-[--w-text-2]">Next Hearing Date</Label>
            <Input
              data-testid="case-input-hearing"
              type="date"
              value={form.next_hearing_date}
              onChange={(e) => setForm({ ...form, next_hearing_date: e.target.value })}
              className="mt-1 border-[--w-border] bg-[--w-bg] text-sm"
            />
          </div>

          <div>
            <Label className="text-xs text-[--w-text-2]">Latest Court Order</Label>
            <Textarea
              data-testid="case-input-order"
              value={form.court_order}
              onChange={(e) => setForm({ ...form, court_order: e.target.value })}
              placeholder="Summary of the latest court order..."
              className="mt-1 border-[--w-border] bg-[--w-bg] text-sm"
              rows={3}
            />
          </div>

          <div>
            <Label className="text-xs text-[--w-text-2]">Documents (Storage Bucket)</Label>
            <div className="mt-1 space-y-2">
              {docs.map((d) => (
                <div key={d.id} className="flex items-center gap-2 rounded-md border border-[--w-border] bg-[--w-bg] px-3 py-2">
                  <FileText className="h-4 w-4 text-blue-600" />
                  <button onClick={() => openDocument(caseId, d.id)} className="flex-1 truncate text-left text-xs text-[--w-text] hover:text-blue-600">
                    {d.original_filename}
                  </button>
                  <button data-testid={`remove-doc-${d.id}`} onClick={() => removeDoc(d.id)} className="text-[--w-text-3] hover:text-red-700">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              {uploadsEnabled ? (
              <>
              <label
                data-testid="upload-doc-label"
                className={`flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-[--w-border-2] bg-[--w-bg] px-3 py-3 text-xs text-[--w-text-2] transition-colors duration-200 hover:border-blue-300 hover:text-blue-600 ${!caseId ? "opacity-50" : ""}`}
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {caseId ? "Upload court order / document" : "Save case to enable uploads"}
                <input type="file" accept=".pdf,.docx,.jpg,.jpeg,.png" className="hidden" onChange={handleUpload} disabled={!caseId || uploading} data-testid="upload-doc-input" />
              </label>
              <p className="text-[10px] text-[--w-text-3]">PDF, Word, JPG or PNG, up to {uploadMaxMb} MB.</p>
              </>
              ) : (
              <p data-testid="uploads-off-notice" className="text-xs text-amber-700">Document uploads are switched off.</p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-[--w-text-2] hover:bg-[--w-card-2] hover:text-[--w-text]">Cancel</Button>
          <Button data-testid="case-save-button" onClick={save} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : caseId ? "Update Record" : "Save to DB"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
